import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { useDebounce } from 'helpers'
import { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { ChevronDown, Edit } from 'react-feather'
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Spinner,
} from 'reactstrap'
import { useFetchPortsQuery } from './portsListApiSlice'
import { Can } from 'context/Acl'
import { BUSINESS_VESSELS, MANAGE } from 'utils/subjectActions'
import PMBTooltip from 'components/PMBTooltip'
import { P } from 'components'
import IconUpdatePort from '../../../../assets/icons/icon-ports.svg'
import { useDispatch } from 'react-redux'
import { updatePort } from './portsListAction'
import { useSnackbar } from 'notistack'

const PortsTable = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [isNoDataFilterAvailable, setIsNoDataFilterAvailable] = useState(false)
  const [modal, setModal] = useState({ edit: false })
  const [payload, setPayload] = useState({})
  const [loading, setLoading] = useState(false)

  // SORT BY FILTER
  const [sortColumn, setSortColumn] = useState('id')
  const [sortDirection, setSortDirection] = useState('DESC')

  const { data, isLoading, refetch, isFetching } = useFetchPortsQuery({
    page: currentPage ?? 0,
    size: rowsPerPage ?? 10,
    searchvalue: search,
    sortby: sortColumn ?? 'id',
    sortdir: sortDirection ?? 'DESC',
  })

  // HANDLE LOADING FILTER
  useEffect(() => {
    if (!isFetching && data?.totalItems === 0) {
      setIsNoDataFilterAvailable(true)
    } else {
      setIsNoDataFilterAvailable(false)
    }
  }, [isFetching, data?.totalItems])

  const handleSort = (column, direction) => {
    setSortColumn(column.selector)
    setSortDirection(direction?.toLowerCase() === 'asc' ? 'ASC' : 'DESC')
  }

  const columns = [
    {
      name: <span>ID</span>,
      selector: 'id',
      sortable: true,
      cell: (row) => {
        return <span>{row?.id ?? '-'}</span>
      },
      minWidth: '100px',
    },
    {
      name: <span>Port Name</span>,
      selector: 'name',
      sortable: true,
      cell: (row) => {
        return <span>{row?.name ?? '-'}</span>
      },
      minWidth: '200px',
    },
    {
      name: <span>District</span>,
      selector: 'district',
      sortable: true,
      cell: (row) => {
        return <span>{row?.district ?? '-'}</span>
      },
      minWidth: '200px',
    },
    {
      name: <span>Latitude</span>,
      selector: 'latitude',
      sortable: true,
      cell: (row) => {
        return <span>{row?.latitude ?? '-'}</span>
      },
      minWidth: '200px',
    },
    {
      name: <span>Longitude</span>,
      selector: 'longitude',
      sortable: true,
      cell: (row) => {
        return <span>{row?.longitude ?? '-'}</span>
      },
      minWidth: '200px',
    },
    {
      name: <span>Certificate</span>,
      selector: 'longitude',
      sortable: true,
      minWidth: '250px',
      cell: (row) => {
        const {
          landingAuthorizationCertificateAgency,
          landingAuthorizationCertificateIdentification,
          landingAuthorizationCertificateStandart,
          landingAuthorizationCertificateValue,
        } = row

        const listItems = [
          landingAuthorizationCertificateAgency,
          landingAuthorizationCertificateIdentification,
          landingAuthorizationCertificateStandart,
          landingAuthorizationCertificateValue,
        ].filter(Boolean)

        return (
          <div>
            {listItems.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {listItems.map((item, idx) => (
                  <li key={idx} style={{ fontSize: '0.875rem' }}>
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <span>-</span>
            )}
          </div>
        )
      },
    },
    {
      name: '',
      minWidth: '80px',
      cell: (row) => {
        return (
          <Can I={MANAGE} this={BUSINESS_VESSELS}>
            <div
              className="d-flex align-items-center"
              style={{ gap: '12px', flexGrow: 1 }}
              data-testid={`port-table-row-${row?.id}`}
            >
              <div id={`vessel-${row?.id}`}>
                <Edit
                  size={18}
                  onClick={() => {
                    setPayload(row)
                    setModal((prevState) => ({ ...prevState, edit: true }))
                  }}
                  style={{ cursor: 'pointer' }}
                  data-testid={`port-row-edit-${row?.id}`}
                />
              </div>
              <PMBTooltip target={`vessel-${row?.id}`} text="Ubah" />
            </div>
          </Can>
        )
      },
    },
  ]

  const customStyles = {
    headCells: {
      style: {
        color: '#667085',
        backgroundColor: '#F9FAFB',
      },
    },
    cells: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
      },
    },
    rows: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
        '&:hover': {
          backgroundColor: '#F9FAFB',
        },
      },
    },
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && search.length === 0) {
      e.preventDefault()
    }
  }

  const debouncedSearch = useDebounce((value) => {
    // setTag({ page: 0, size: 10 })
    setCurrentPage(0)
    setRowsPerPage(10)
    setSearch(value)
    refetch()
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const onHandleUpdate = async () => {
    try {
      setLoading(true)
      const result = await dispatch(updatePort(payload)).unwrap()
      refetch()
      enqueueSnackbar('Data berhasil diubah!', {
        variant: 'success',
      })
      setModal((prevState) => ({ ...prevState, edit: false }))
    } catch (err) {
      enqueueSnackbar('Data gagal diubah!', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs breadCrumbActive="Data Ports" />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            customStyles={customStyles}
            columns={columns}
            data={data?.items}
            progressPending={isLoading || isFetching}
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={false}
            paginationTotalRows={data?.totalItems}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            sortIcon={<ChevronDown />}
            sortServer
            onSort={handleSort}
            className="border p-0 border-1 rounded-top"
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isNoDataFilterAvailable
                  ? 'Tidak ada data yang sesuai dengan filter Anda.'
                  : 'Belum ada daftar ports yang didaftarkan'}
              </p>
            }
            pagination
            paginationServer
            paginationComponent={(props) => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                setCurrentPage={setCurrentPage}
                setRowsPerPage={setRowsPerPage}
                props={props}
                testId="port-pagination"
              />
            )}
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">Daftar Ports</h5>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label={'Cari'}
                    value={searchInput}
                    placeholder="Cari nama port..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth="25%"
                    onKeyDown={handleKeyDown}
                    testId="port-search-input"
                  />
                </div>
              </div>
            }
          />
        </CardBody>
      </Card>
      {renderModal()}
    </div>
  )

  function renderModal() {
    return (
      <>
        <Modal
          centered
          isOpen={modal?.edit}
          toggle={() =>
            setModal((prevState) => ({ ...prevState, edit: false }))
          }
          data-testid="port-modal-edit"
        >
          <ModalBody style={{ overflowY: 'auto', maxHeight: '90vh' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  backgroundColor: '#ecfaff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#d6eaf1',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={IconUpdatePort}
                    loading="lazy"
                    style={{
                      width: 24,
                      height: 24,
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
            </div>
            <P size="medium">Ubah data Port</P>
            <div className="mt-3">
              <FormGroup>
                <Label className="text-sm">Port name</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan nama port"
                  autoComplete="off"
                  name="name"
                  id="name"
                  data-testid="port-form-name"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.name ?? null}
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-sm">District</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan district"
                  autoComplete="off"
                  name="district"
                  id="district"
                  data-testid="port-form-district"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.district ?? null}
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-sm">Latitude</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan latitude"
                  autoComplete="off"
                  name="latitude"
                  id="latitude"
                  data-testid="port-form-latitude"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.latitude ?? null}
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-sm">Longitude</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan longitude"
                  autoComplete="off"
                  name="longitude"
                  id="longitude"
                  data-testid="port-form-longitude"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.longitude ?? null}
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-sm">
                  Landing Authorization Certificate Agency
                </Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan nama agensi sertifikat"
                  autoComplete="off"
                  name="landingAuthorizationCertificateAgency"
                  id="landingAuthorizationCertificateAgency"
                  data-testid="port-form-certificate-agency"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.landingAuthorizationCertificateAgency ?? null}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">
                  Landing Authorization Certificate Standard
                </Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan standar sertifikat"
                  autoComplete="off"
                  name="landingAuthorizationCertificateStandart"
                  id="landingAuthorizationCertificateStandart"
                  data-testid="port-form-certificate-standart"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={
                    payload?.landingAuthorizationCertificateStandart ?? null
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">
                  Landing Authorization Certificate Value
                </Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan nilai sertifikat"
                  autoComplete="off"
                  name="landingAuthorizationCertificateValue"
                  id="landingAuthorizationCertificateValue"
                  data-testid="port-form-certificate-value"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.landingAuthorizationCertificateValue ?? null}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">
                  Landing Authorization Certificate Identification
                </Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan identifikasi sertifikat"
                  autoComplete="off"
                  name="landingAuthorizationCertificateIdentification"
                  id="landingAuthorizationCertificateIdentification"
                  data-testid="port-form-certificate-identification"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={
                    payload?.landingAuthorizationCertificateIdentification ??
                    null
                  }
                />
              </FormGroup>

              <div className="d-flex gap-2">
                <Button
                  color="primary"
                  outline
                  style={{ flex: 0.49 }}
                  onClick={() =>
                    setModal((prevState) => ({ ...prevState, edit: false }))
                  }
                  data-testid="port-modal-edit-cancel-button"
                >
                  <P>Batal</P>
                </Button>
                <Button
                  color="primary"
                  style={{ flex: 0.49 }}
                  onClick={onHandleUpdate}
                  data-testid="port-modal-edit-submit-button"
                >
                  <P>Simpan</P>
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </>
    )
  }
}

export default PortsTable
